import { useCallback, memo, useMemo } from 'react';
import {
  ReactHookFormAutocomplete,
  Button,
  Grid,
  Popper,
  PopperActions,
  PopperContent,
  PopperTitle,
  Typography,
  useTranslations,
  useHeroSnackbar,
  usePubSub,
  ReactHookFormDateRangePickerWithTextField,
  formatDateRanges,
  initDateRange,
  DateRange,
  IISODateRange,
  buildFilterOptionsAsObject,
  IAutocompleteProps,
  formatSupporterEmail,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import { GetEmployeeAccountsV2ResponseModel } from '@uniqkey-backend-organization-web/api-client';
import PubSubEventEnum from '../../../../../../enums/PubSubEventEnum';
import useEmployeeAccountsAPI from '../../../../../../hooks/useEmployeeAccountsAPI';
import { logException } from '../../../../../../services/sentryService';
import { IGetAuditLogsParams } from '../../../../../../hooks/useAuditLogsAPI/interfaces';

export interface IAuditLogsListFilterSubmitResult {
  employeeAccount: IEmployeeAccountOption | undefined;
  dateRange: IISODateRange | undefined;
  onlySupporterLogs: IGetAuditLogsParams['onlySupporterLogs'];
}

interface IAuditLogsListFilterProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onSubmit: (result: IAuditLogsListFilterSubmitResult) => void;
  onClose: () => void;
  initialValues: IAuditLogsListFilterSubmitResult
}

interface IEmployeeAccountOption {
  value: GetEmployeeAccountsV2ResponseModel['employeeAccountId'];
  label: GetEmployeeAccountsV2ResponseModel['email'];
}

type TOnlySupporterLogsOption = {
  label: string;
  value: IAuditLogsListFilterSubmitResult['onlySupporterLogs'];
} | null;

interface IFormValues {
  employeeAccount: IEmployeeAccountOption | null;
  dateRange: DateRange;
  onlySupporterLogs: TOnlySupporterLogsOption;
}

const POPPER_MIN_WIDTH = { minWidth: 500 };

const AuditLogsListFilter = (props: IAuditLogsListFilterProps) => {
  const {
    isOpen, anchorEl, onSubmit, onClose, initialValues,
  } = props;
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getEmployeeAccountsForLookup } = useEmployeeAccountsAPI();

  const [onlySupporterLogsOptions, onlySupporterLogsOptionsAsObject] = useMemo(() => {
    const options = [
      { label: t('auditLogsFilter.onlySupporterLogs.values.enabled'), value: true },
      { label: t('auditLogsFilter.onlySupporterLogs.values.disabled'), value: false },
    ];
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const {
    handleSubmit, control, reset, setValue, formState: { isDirty },
  } = useForm<IFormValues>({
    defaultValues: {
      employeeAccount: initialValues.employeeAccount ?? null,
      dateRange: initDateRange(initialValues.dateRange),
      onlySupporterLogs: onlySupporterLogsOptionsAsObject[
        initialValues.onlySupporterLogs?.toString() as keyof typeof onlySupporterLogsOptionsAsObject
      ] ?? null,
    },
  });

  const handleGetEmployeeAccountsRequest = useCallback(async (searchQuery: string) => {
    try {
      const { data } = await getEmployeeAccountsForLookup({
        page: 1, pageLength: 100, searchQuery, showSupporters: true,
      });
      return data;
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'AuditLogsListFilter/handleGetEmployeeAccountsRequest exception',
      });
      return [];
    }
  }, [getEmployeeAccountsForLookup, showError, t]);

  const handleGetEmployeeAccountsResponseParser = useCallback(
    (employeeAccountsToParse: GetEmployeeAccountsV2ResponseModel[]) => employeeAccountsToParse.map(
      (employeeAccount: GetEmployeeAccountsV2ResponseModel) => ({
        value: employeeAccount.employeeAccountId,
        label: formatSupporterEmail(employeeAccount.email, 'supporter.emailSuffix'),
      }),
    ),
    [],
  );

  const getOptionKey = useCallback<
    NonNullable<IAutocompleteProps['getOptionKey']>
  >((option) => option.value.toString(), []);

  const handleOnSubmit = useCallback((value: IFormValues): void => {
    const { employeeAccount, dateRange, onlySupporterLogs } = value;
    const processedDateRange = dateRange.from && dateRange.to
      ? formatDateRanges(dateRange)
      : undefined;
    onSubmit({
      employeeAccount: employeeAccount ?? undefined,
      dateRange: processedDateRange,
      onlySupporterLogs: onlySupporterLogs?.value,
    });
    onClose();
    reset(value); // set default values to the selected ones to reset isDirty
  }, [reset, onSubmit, onClose]);

  const clearAll = useCallback(() => {
    setValue('employeeAccount', null, { shouldDirty: true });
    setValue('dateRange', initDateRange(), { shouldDirty: true });
    setValue('onlySupporterLogs', null, { shouldDirty: true });
  }, [setValue]);

  const handleOnFadeExited = useCallback(() => {
    reset(); // reset form values when Popper is closed and animation has finished
  }, [reset]);

  const handleReset = useCallback(() => {
    reset({
      employeeAccount: null,
      dateRange: initDateRange(),
      onlySupporterLogs: null,
    });
  }, [reset]);

  usePubSub(PubSubEventEnum.RESET_FILTER, handleReset);

  return (
    <Popper
      anchorEl={anchorEl}
      offsetX={-8}
      offsetY={16}
      placement="bottom-start"
      open={isOpen}
      sx={POPPER_MIN_WIDTH}
      onFadeExited={handleOnFadeExited}
    >
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <PopperTitle onClose={onClose}>
          {t('common.filterBy')}
        </PopperTitle>
        <PopperContent>
          <Grid container direction="column" rowSpacing={2}>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('auditLogsFilter.date.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormDateRangePickerWithTextField
                  name="dateRange"
                  control={control}
                  t={t}
                  placement="bottom-start"
                  fullWidth
                  selectLabelText={t('auditLogsFilter.date.placeholder')}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('auditLogsFilter.user.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  t={t}
                  name="employeeAccount"
                  placeholder={t('auditLogsFilter.user.placeholder')}
                  dataSourceRequest={handleGetEmployeeAccountsRequest}
                  dataSourceResponseParser={handleGetEmployeeAccountsResponseParser}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('auditLogsFilter.onlySupporterLogs.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="onlySupporterLogs"
                  options={onlySupporterLogsOptions}
                  getOptionKey={getOptionKey}
                  placeholder={t('auditLogsFilter.onlySupporterLogs.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
          </Grid>
        </PopperContent>
        <PopperActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button disabled={!isDirty} fullWidth type="submit">{t('common.apply')}</Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAll}
              >
                {t('common.clearAll')}
              </Button>
            </Grid>
          </Grid>
        </PopperActions>
      </form>
    </Popper>
  );
};

export default memo(AuditLogsListFilter);
